import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faCalculator } from '@fortawesome/free-solid-svg-icons';
import Container from '../ui/Container';
import Button from '../ui/Button';
import MobileNav from './MobileNav';
import { primaryNavigation } from '../../utils/navigation';
import businessInfo from '../../utils/businessInfo';
import ROUTES from '../../utils/routes';
import { isSamePath } from '../../utils/linkUtils';

/**
 * Sticky site header.
 *
 * Desktop and tablet show the inline navigation; below the md breakpoint the
 * inline list is hidden in CSS and the hamburger toggle is revealed, so only
 * one navigation is ever reachable at a time.
 */
const Header = () => {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const toggleRef = useRef(null);
  const { pathname } = useLocation();

  // Lock page scroll behind the drawer without leaking the class on unmount.
  useEffect(() => {
    if (isNavOpen) {
      document.body.classList.add('is-nav-open');
    } else {
      document.body.classList.remove('is-nav-open');
    }
    return () => document.body.classList.remove('is-nav-open');
  }, [isNavOpen]);

  const closeNav = () => setIsNavOpen(false);

  return (
    <header className="site-header">
      <Container className="site-header__inner">
        <Link
          to={ROUTES.home}
          className="site-brand"
          aria-label={`${businessInfo.name} home`}
        >
          <span className="site-brand__mark" aria-hidden="true">
            <FontAwesomeIcon icon={faCalculator} />
          </span>
          <span className="site-brand__text">
            <span className="site-brand__name">{businessInfo.name}</span>
            <span className="site-brand__tagline">{businessInfo.tagline}</span>
          </span>
        </Link>

        <nav className="site-nav" aria-label="Primary">
          <ul className="site-nav__list">
            {primaryNavigation.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    `site-nav__link${isActive ? ' is-active' : ''}`
                  }
                  aria-current={isSamePath(pathname, item.to) ? 'page' : undefined}
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="site-header__actions">
          <Button
            to={ROUTES.contact}
            variant="primary"
            size="md"
            className="site-header__cta"
          >
            Schedule a Consultation
          </Button>

          <button
            type="button"
            ref={toggleRef}
            className="site-header__toggle"
            onClick={() => setIsNavOpen((open) => !open)}
            aria-expanded={isNavOpen}
            aria-controls="mobile-navigation"
            aria-label={isNavOpen ? 'Close navigation menu' : 'Open navigation menu'}
          >
            <FontAwesomeIcon icon={faBars} aria-hidden="true" />
          </button>
        </div>
      </Container>

      <MobileNav
        open={isNavOpen}
        onClose={closeNav}
        items={primaryNavigation}
        toggleRef={toggleRef}
      />
    </header>
  );
};

export default Header;
